const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type Options = { method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"; json?: unknown; file?: Blob };

function detailMessage(body: unknown, fallback: string): string {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      // FastAPI-Validierungsfehler: [{ loc, msg, type }, ...]
      const messages = detail
        .map((item) => (item && typeof item === "object" && "msg" in item ? String(item.msg) : ""))
        .filter(Boolean);
      if (messages.length > 0) return messages.join(", ");
    }
  }
  return fallback;
}

/**
 * Aufruf der Backend-API im Browser. `file` schickt eine Datei unverändert als Anfrage-Inhalt (Import). Das Session-Cookie wird mitgeschickt (`credentials: "include"`,
 * CORS ist im Backend dafür freigegeben). Fehler werden zu `ApiError` mit verständlicher Meldung.
 */
export async function api<T>(path: string, { method = "GET", json, file }: Options = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      credentials: "include",
      headers:
        file !== undefined
          ? { "Content-Type": "application/octet-stream" }
          : json === undefined
            ? undefined
            : { "Content-Type": "application/json" },
      body: file !== undefined ? file : json === undefined ? undefined : JSON.stringify(json),
    });
  } catch {
    throw new ApiError(0, "Die API ist nicht erreichbar. Läuft das Backend?");
  }

  if (response.status === 204) return undefined as T;
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(response.status, detailMessage(body, `Anfrage fehlgeschlagen (HTTP ${response.status}).`));
  }
  return body as T;
}

/** Dateiname aus `Content-Disposition: attachment; filename="…"`, sonst der Ersatzname. */
export function filenameFrom(header: string | null, fallback: string): string {
  const match = header ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header) : null;
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/** Datei per GET holen (mit Session-Cookie), z. B. das Briefing-PDF. Fehler werden zu `ApiError`. */
export async function apiDownload(path: string, fallbackName = "download"): Promise<{ blob: Blob; filename: string }> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { credentials: "include" });
  } catch {
    throw new ApiError(0, "Die API ist nicht erreichbar. Läuft das Backend?");
  }
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    throw new ApiError(response.status, detailMessage(body, `Download fehlgeschlagen (HTTP ${response.status}).`));
  }
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("content-disposition"), fallbackName),
  };
}

export function loginUrl(next: string): string {
  return `/login?next=${encodeURIComponent(next)}`;
}

/** Nur interne Pfade zulassen, sonst ließe sich das Ziel nach dem Login für Weiterleitungen missbrauchen. */
export function safeNext(next: string | null, fallback = "/"): string {
  return next && next.startsWith("/") && !next.startsWith("//") && !next.includes("\\") ? next : fallback;
}
